"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardSection = void 0;
const ui_1 = require("@app/component/ui");
const data_grid_1 = require("@app/component/ui/data-grid");
const InfoBoxV2_1 = require("@app/component/ui/info-box/InfoBoxV2");
const Tooltip_1 = require("@app/component/ui/tooltip/Tooltip");
const const_1 = require("@app/const");
const graphql_1 = require("@app/gql-types/graphql");
const user_details_1 = require("@app/reducer/user-details");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const format_hours_1 = require("@app/util/format-hours");
const use_go_to_detailed_users_report_1 = require("@app/util/hooks/use-go-to-detailed-users-report");
const use_go_to_user_warnings_report_1 = require("@app/util/hooks/use-go-to-user-warnings-report");
const use_plugin_1 = require("@app/util/hooks/use-plugin");
const useActivatePlugin_1 = require("@app/util/hooks/useActivatePlugin");
const useSortedContracts_1 = require("@app/util/hooks/useSortedContracts");
const use_modal_1 = require("@app/util/modal/use-modal");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const terminal_generate_pin_for_user_1 = require("../../ac/terminal-generate-pin-for-user");
const vacation_funds_1 = require("../../request/vacation-funds");
const current_contract_label_1 = require("../../util/current-contract-label");
const email_is_default_1 = require("../../util/email-is-default");
const employee_card_context_1 = require("../../util/employee-card-context");
const get_first_or_undefined_1 = require("../../util/get-first-or-undefined");
const useEmployeeCardFooterContent_1 = require("../../util/hooks/useEmployeeCardFooterContent");
const useUserDetailsContracts_1 = require("../../util/hooks/useUserDetailsContracts");
const useUserDetailsCustomAttributes_1 = require("../../util/hooks/useUserDetailsCustomAttributes");
const useUserDetailsCustomData_1 = require("../../util/hooks/useUserDetailsCustomData");
const useUserDetailsEmployeeStats_1 = require("../../util/hooks/useUserDetailsEmployeeStats");
const useUserDetailsPositions_1 = require("../../util/hooks/useUserDetailsPositions");
const useUserDetailsStats_1 = require("../../util/hooks/useUserDetailsStats");
const useUserDetailsTimeOffs_1 = require("../../util/hooks/useUserDetailsTimeOffs");
const useUserEmployeeWarnings_1 = require("../../util/hooks/useUserEmployeeWarnings");
const employee_header_activation_1 = require("../employee-header-activation");
const DashboardSection = () => {
    const dispatch = (0, store_1.useRootDispatch)();
    const { workspace, employee, period, selectedMonth, setSelectedMonth, userId } = (0, employee_card_context_1.useEmployeeCard)();
    const { closeModal } = (0, use_modal_1.useModal)();
    const { canRead, canWrite } = (0, util_1.useAccessRole)();
    const employeeTimeOffs = (0, useUserDetailsTimeOffs_1.useUserDetailsTimeOffs)(period);
    const roleStats = (0, useUserDetailsStats_1.useUserDetailsStats)(period.end);
    const [userCustomAttributes] = (0, useUserDetailsCustomAttributes_1.useUserDetailsCustomAttributes)();
    const [customData] = (0, useUserDetailsCustomData_1.useUserDetailsCustomData)();
    const userContracts = (0, useUserDetailsContracts_1.useUserDetailsContracts)();
    const userPositions = (0, useUserDetailsPositions_1.useUserDetailsPositions)();
    const employeeStats = (0, useUserDetailsEmployeeStats_1.useUserDetailsEmployeeStats)();
    // const userVacations = useUserVacations()
    const contractTypes = react_1.default.useMemo(() => [
        ...(workspace?.contractTypes ?? []),
        {
            id: 'external',
            name: (0, util_1.t)('ORG_SP_SETTINGS_MODULES_EXTERNAL'),
            rules: {
                vacationFund: true,
                fixedWorkHours: false,
            },
        },
    ], [workspace?.contractTypes]);
    const enableGenerateTerminalPINWithID = (0, util_1.isDefined)(customData?.employeeId) &&
        (0, util_1.isNotNull)(customData?.employeeId) &&
        customData?.employeeId.toString().length === 4;
    const { activeContracts } = (0, useSortedContracts_1.useSortedContracts)(userContracts);
    const userAttributes = workspace?.userAttributes ?? [];
    const stitchedAttributes = userAttributes.map((ua) => {
        const customAttribute = userCustomAttributes?.find((ca) => ca.attributeId === ua?.id);
        return {
            ...ua,
            value: customAttribute?.value,
        };
    });
    const customAttributes = react_1.default.useMemo(() => stitchedAttributes.filter((sa) => (0, util_1.isDefined)(sa.hidden) && !sa.hidden), [stitchedAttributes]);
    // First try and find a contract with fixed work hours, if there is none, then find whatever is available
    const prefferedContract = activeContracts.find((contract) => contract.compiled?.rules.fixedWorkHours === true) ??
        activeContracts.find((contract) => contract.compiled?.rules.fixedWorkHours === false);
    const contractType = contractTypes?.find((c) => c.id === prefferedContract?.type);
    const contractInSelectedMonth = userContracts
        ? userContracts.filter((contract) => util_1.calendarUtil.isPeriodsOverlap(contract?.period, period))
        : [];
    const hasFixedHoursContract = contractTypes
        ?.filter((ctp) => ctp.id === contractType?.id ||
        contractInSelectedMonth.some((item) => ctp.id === item.type))
        .some((item) => item?.rules?.fixedWorkHours);
    const hasContractWithVacationFund = contractTypes
        ?.filter((ctp) => ctp.id === contractType?.id ||
        contractInSelectedMonth.some((item) => ctp.id === item.type))
        .some((item) => item?.rules?.vacationFund);
    const wsBalancingPeriod = workspace?.settings?.balancingPeriod;
    const wsBalancingPeriodRelevant = !!wsBalancingPeriod &&
        (wsBalancingPeriod.unit !== 'month' || wsBalancingPeriod.amount !== 1);
    const wsDisplayVacationsAsDays = workspace?.displayVacationsAsDays;
    const attendanceHours = util_1.timeUtil.asHour(roleStats?.monthPlannedMinutes);
    const monthContractHours = util_1.timeUtil.asHour(roleStats?.monthContractMinutes);
    const monthOverTimeHours = util_1.timeUtil.asHour(roleStats?.monthOverTimeMinutes);
    const balancingPeriodPlannedHours = util_1.timeUtil.asHour(roleStats?.balancingPeriodPlannedMinutes);
    const balancingPeriodContractHours = util_1.timeUtil.asHour(roleStats?.balancingPeriodContractMinutes);
    const balancingPeriodOvertimeHours = util_1.timeUtil.asHour(roleStats?.balancingPeriodOverTimeMinutes);
    const workTimeHours = (employeeStats?.workMinutes ?? 0) / 60;
    const timeOffHours = employeeTimeOffs?.reduce((acc, timeOff) => acc + util_1.timeUtil.asHour(timeOff.workMinutes), 0) ?? 0;
    const vacationLabels = (0, util_1.t)('EMPLOYEE_PROFILE_CARD_OVERVIEW_WIDGET_CHOSEN').split('/');
    const userVacation = (0, store_1.useRootSelector)((state) => state.userDetails.vacation);
    const userVacationAmount = userVacation?.reduce((acc, curr) => acc + (curr.amount ?? 0), 0);
    const vacationFund = wsDisplayVacationsAsDays
        ? userVacationAmount
        : (userVacationAmount ?? 0) * 60;
    const vacationTaken = wsDisplayVacationsAsDays
        ? roleStats?.yearVacationDays
        : roleStats?.yearVacationMinutes;
    const vacationWidgetProgress = hasContractWithVacationFund
        ? vacationFund === 0
            ? 100
            : util_1.numberUtil.calculatePercentage({
                baseValue: vacationFund ?? 0,
                currentValue: vacationTaken ?? 0,
            })
        : undefined;
    const vacationTakenDisplayValue = wsDisplayVacationsAsDays
        ? `${util_1.numberUtil.round2decimals(vacationTaken)} ${(0, util_1.t)('DAYS_COUNT')}`
        : util_1.timeUtil.asHour(vacationTaken);
    const vacationFundDisplayValue = wsDisplayVacationsAsDays
        ? `${hasContractWithVacationFund ? util_1.numberUtil.round2decimals(vacationFund) : 0} ${(0, util_1.t)('DAYS_COUNT')}`
        : hasContractWithVacationFund
            ? util_1.timeUtil.asHour(vacationFund)
            : 0;
    // console.log('[DashboardSection] vacation widget calculations', {
    //   hasContractWithVacationFund,
    //   wsDisplayVacationsAsDays,
    //   vacationFund,
    //   vacationTaken,
    //   vacationWidgetProgress,
    //   vacationTakenDisplayValue,
    //   vacationFundDisplayValue,
    // })
    const goToDetailedReport = (0, use_go_to_detailed_users_report_1.useGoToDetailedUsersReport)(selectedMonth);
    let _userPositions = userPositions
        ?.filter((p) => !p.archived)
        .filter((p) => p.workspaceId === workspace?.id);
    if (_userPositions?.length)
        _userPositions = util_1.sortUtil.sortByKey(_userPositions, 'name');
    const { isPluginEnabled } = (0, use_plugin_1.usePlugin)();
    const isTerminalPluginEnabled = isPluginEnabled('terminal');
    const { displayContent } = (0, useEmployeeCardFooterContent_1.useEmployeeCardFooterContent)(react_1.default.createElement("div", { style: { display: 'flex' } },
        react_1.default.createElement(ui_1.Button, { onClick: () => {
                closeModal();
            } }, (0, util_1.t)('CLOSE')),
        react_1.default.createElement(ui_1.Button, { style: 'CONTAINED', disabled: true }, (0, util_1.t)('SAVE'))));
    const { ActivatePlugin: ActivateTerminalPlugin } = (0, useActivatePlugin_1.useActivatePlugin)('terminal');
    const emailIsBad = (0, email_is_default_1.emailIsDefault)(employee.user?.email ?? '');
    const userEmployeeWarnings = (0, useUserEmployeeWarnings_1.useUserEmployeeWarnings)();
    const filterOutDummyWarnings = react_1.default.useCallback((w) => {
        if ((0, util_1.isUndefined)(w))
            return [];
        return w.filter((warning) => warning.name !== 'dummy');
    }, []);
    const goToUserWarningsReport = (0, use_go_to_user_warnings_report_1.useGoToUserWarningsReport)(selectedMonth);
    react_1.default.useEffect(() => {
        const action = async () => {
            const _userVacations = await (0, vacation_funds_1.queryVacationFunds)({
                workspaceId: workspace?.id ?? '',
                userId: userId ?? '',
            });
            dispatch((0, user_details_1.setUserVacation)(_userVacations ?? []));
        };
        void action();
    }, [workspace?.id, userId]);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_8 }),
        react_1.default.createElement(employee_header_activation_1.EmployeeHeaderActivation, null),
        react_1.default.createElement("div", { style: {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                width: '100%',
            } },
            employee.user?.dummy && (react_1.default.createElement(ui_1.Alert, { size: 'FULL_WIDTH', type: 'WARNING', text: (0, util_1.t)('EMPLOYEE_NOT_ACTIVATED') })),
            (filterOutDummyWarnings((0, maybe_unwrap_1.unwrapMaybe)(userEmployeeWarnings?.[0]?.warnings))
                .length ?? -1) > 0 && (react_1.default.createElement(ui_1.Alert, { size: 'FULL_WIDTH', type: 'ERROR', text: (0, util_1.t)(`EMPLOYEE_WARNINGS.${filterOutDummyWarnings((0, maybe_unwrap_1.unwrapMaybe)(userEmployeeWarnings?.[0]?.warnings))[0]?.name ?? ''}.title`), buttonText: (0, util_1.t)('WARNING_DETAIL'), onClick: () => goToUserWarningsReport([userId ?? '']) }))),
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_8 }),
        react_1.default.createElement("div", { style: {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                width: '100%',
            } },
            react_1.default.createElement(InfoBoxV2_1.InfoBoxV2, { size: 'full-width', header: react_1.default.createElement(ui_1.Flex, { flex: 1, justify: 'space-between' },
                    react_1.default.createElement(ui_1.MonthSelect, { returnType: ui_1.MonthSelect.RETURN_TYPES.STRING, style: ui_1.MonthSelect.STYLES.COMPACT, value: selectedMonth, onChange: setSelectedMonth, upperCase: true, forbiddenFuture: true }),
                    canRead(const_1.PERMISSIONS.BACKOFFICE.REPORT_EMPLOYEE) && (react_1.default.createElement("div", { onClick: () => goToDetailedReport([userId ?? '']), style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                        } },
                        react_1.default.createElement("div", { style: { marginRight: '2rem' } },
                            react_1.default.createElement("a", { style: { color: '#0045ff' }, type: 'button' }, (0, util_1.t)('DETAILED_REPORT'))),
                        react_1.default.createElement("span", null,
                            react_1.default.createElement("a", { style: { color: '#0045ff' }, type: 'button' }, '\u203a'))))), grow: 1, basis: 40, cols: [
                    {
                        data: [
                            {
                                label: (0, util_1.t)('ATTENDANCE'),
                                value: attendanceHours,
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                            },
                            {
                                label: (0, util_1.t)('FOND'),
                                value: monthContractHours,
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                            },
                            {
                                label: (0, util_1.t)('OVERTIME'),
                                value: `(+${(0, format_hours_1.formatHours)(monthOverTimeHours)})`,
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.TEXT,
                                helpText: (0, util_1.t)('ATTEN_WIDG_HELP_OVERTIME'),
                            },
                            {
                                label: (0, util_1.t)('DIFFERENCE'),
                                value: attendanceHours - monthContractHours,
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                fieldType: InfoBoxV2_1.InfoBoxV2.FIELD_TYPES.DIFFERENCE,
                            },
                        ],
                    },
                    {
                        data: [
                            InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                            InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                            InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                            {
                                label: (0, util_1.t)('ATTEN_WIDG_HOURS'),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                value: workTimeHours,
                            },
                            {
                                label: (0, util_1.t)('ATTEN_WIDG_UNAVS'),
                                dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                value: timeOffHours,
                            },
                        ],
                    },
                ] }),
            react_1.default.createElement("div", { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' } },
                wsBalancingPeriodRelevant && hasFixedHoursContract && (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(InfoBoxV2_1.InfoBoxV2, { title: (0, util_1.t)('BALANCING_PERIOD'), grow: 1, progress: balancingPeriodContractHours === 0
                            ? 100
                            : util_1.numberUtil.calculatePercentage({
                                baseValue: balancingPeriodContractHours,
                                currentValue: balancingPeriodPlannedHours,
                            }), size: 'full-width', cols: [
                            {
                                data: [
                                    {
                                        label: (0, util_1.t)('ATTENDANCE'),
                                        value: balancingPeriodPlannedHours,
                                        dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                    },
                                    {
                                        label: (0, util_1.t)('FOND'),
                                        value: balancingPeriodContractHours,
                                        dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                    },
                                    {
                                        label: (0, util_1.t)('OVERTIME'),
                                        value: `(+${(0, format_hours_1.formatHours)(balancingPeriodOvertimeHours)})`,
                                        dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.TEXT,
                                    },
                                ],
                            },
                        ] }))),
                react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(InfoBoxV2_1.InfoBoxV2, { title: (0, util_1.t)('EMPLOYEE_PROFILE_CARD_OVERVIEW_WIDGET_HOLIDAY'), grow: 1, progress: vacationWidgetProgress, size: 'full-width', cols: [
                            {
                                data: [
                                    InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                                    InfoBoxV2_1.InfoBoxV2.EMPTY_LINE,
                                    wsDisplayVacationsAsDays
                                        ? {
                                            label: vacationLabels[0],
                                            value: vacationTakenDisplayValue,
                                            dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.TEXT,
                                        }
                                        : {
                                            label: vacationLabels[0],
                                            value: vacationTakenDisplayValue,
                                            dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                        },
                                    wsDisplayVacationsAsDays
                                        ? {
                                            label: vacationLabels[1],
                                            value: vacationFundDisplayValue,
                                            dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.TEXT,
                                        }
                                        : {
                                            label: vacationLabels[1],
                                            value: vacationFundDisplayValue,
                                            dataType: InfoBoxV2_1.InfoBoxV2.DATA_TYPES.HOURS,
                                        },
                                ],
                            },
                        ] })))),
        react_1.default.createElement(ui_1.Spacing, { type: ui_1.Spacing.TYPES.VERTICAL, size: ui_1.Spacing.SIZES.SIZE_16 }),
        react_1.default.createElement(data_grid_1.DataGrid, { columns: [
                {
                    cells: [
                        {
                            label: (0, util_1.t)('CURRENT_CONTRACTS'),
                            value: (0, current_contract_label_1.currentContractLabel)((0, maybe_unwrap_1.unwrapMaybe)(contractType?.name), (0, maybe_unwrap_1.unwrapMaybe)(prefferedContract?.type), prefferedContract?.options?.minutesOfWorkPerMonth),
                        },
                        {
                            label: (0, util_1.t)('JOB_POSITION'),
                            value: (react_1.default.createElement("div", { style: {
                                    display: 'flex',
                                    gap: '0.25rem',
                                    width: '100%',
                                    height: '100%',
                                    alignItems: 'center',
                                    overflowX: 'auto',
                                } }, _userPositions?.map((p) => (react_1.default.createElement(ui_1.Position, { key: p.id, pos: p, size: 'small' }))))),
                        },
                        {
                            label: (0, util_1.t)('EMAIL'),
                            value: employee.user?.dummy || emailIsBad ? null : employee.user?.email,
                        },
                        {
                            label: (0, util_1.t)('PHONE'),
                            value: util_1.miscUtil.formatPhoneNumber((0, get_first_or_undefined_1.getFirstOrUndefined)([
                                (0, maybe_unwrap_1.unwrapMaybe)(customData?.telephone === ''
                                    ? undefined
                                    : customData?.telephone),
                                (0, maybe_unwrap_1.unwrapMaybe)(employee?.user?.telephone),
                            ])),
                        },
                        isTerminalPluginEnabled
                            ? {
                                label: (0, util_1.t)('TERMINAL') + ' ' + (0, util_1.t)('PIN'),
                                value: (react_1.default.createElement("div", { style: {
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0',
                                    } },
                                    react_1.default.createElement("div", null, employee.terminalPIN ?? '-'),
                                    react_1.default.createElement("div", { style: { display: 'flex', gap: '0.5rem' } },
                                        react_1.default.createElement(Tooltip_1.Tooltip, { position: 'top', text: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('TERMINAL_PIN_HASH_TOOLTIP')), anchor: react_1.default.createElement(ui_1.Icon, { color: 'primary', ico: 'hash', disabled: !enableGenerateTerminalPINWithID ||
                                                    !canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES), onClick: async () => await dispatch((0, terminal_generate_pin_for_user_1.terminalGeneratePINForUser)({
                                                    workspaceId: workspace?.id ?? '',
                                                    userId: userId ?? '',
                                                    useEmployeeId: enableGenerateTerminalPINWithID,
                                                })) }) }),
                                        react_1.default.createElement(Tooltip_1.Tooltip, { position: 'top', text: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('TERMINAL_PIN_NEW_TOOLTIP')), anchor: react_1.default.createElement(ui_1.Icon, { color: canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES)
                                                    ? 'primary'
                                                    : 'inherit', disabled: !canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES), ico: 'change', onClick: async () => {
                                                    if (canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES)) {
                                                        await dispatch((0, terminal_generate_pin_for_user_1.terminalGeneratePINForUser)({
                                                            workspaceId: workspace?.id ?? '',
                                                            userId: userId ?? '',
                                                        }));
                                                    }
                                                } }) })))),
                            }
                            : {
                                label: (0, util_1.t)('TERMINAL') + ' ' + (0, util_1.t)('PIN'),
                                value: react_1.default.createElement(ActivateTerminalPlugin, null),
                            },
                    ],
                },
                {
                    cells: customAttributes?.map((attr) => {
                        if (attr?.type === graphql_1.CustomAttributeValueType.Boolean) {
                            if ((0, util_1.isUndefined)(attr.value) || (0, util_1.isNull)(attr.value)) {
                                return {
                                    label: '-',
                                    value: null,
                                };
                            }
                            return {
                                label: attr.name ?? '-',
                                value: attr.value ? (0, util_1.t)('YES') : (0, util_1.t)('NO'),
                            };
                        }
                        if (attr?.type === graphql_1.CustomAttributeValueType.Date) {
                            return {
                                label: attr.name ?? '-',
                                value: (0, moment_1.default)(attr.value, 'YYYY-MM-DD').isValid()
                                    ? (0, moment_1.default)(attr.value, 'YYYY-MM-DD').format('DD.MM.YYYY')
                                    : null,
                            };
                        }
                        if (attr?.type === graphql_1.CustomAttributeValueType.Number) {
                            return {
                                label: attr.name ?? '-',
                                value: util_1.numberUtil.localizeNumber(attr.value),
                            };
                        }
                        return {
                            label: attr.name ?? '-',
                            value: attr.value,
                        };
                    }) ?? [],
                },
            ] }),
        displayContent()));
};
exports.DashboardSection = DashboardSection;
